import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { SendSmsStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { TemplateType } from "@redotech/redo-marketing-templates-common/email-builder";
import { SmsTemplate } from "@redotech/redo-marketing-templates-common/sms-template";
import { getImageAttachment } from "@redotech/redo-marketing-templates-common/sms-template-utils";
import { RedoMerchantSmsTemplatesRpcClientContext } from "@redotech/redo-merchant-app-common/sms-templates-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import { InvalidStepsList } from "../components/invalid-steps-list";
import { AutomationAbTestEditor } from "./automation-ab-test-editor";
import { AutomationAbTestInspector } from "./automation-ab-test-inspector";
import { AutomationAbTestResultsPanel } from "./automation-ab-test-results-panel";
import { SmsPreviewContent } from "./common/sms-preview-content";
import { useAbTestEditor } from "./common/use-ab-test-editor";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";
import { VariantPlaceholder } from "./common/variant-placeholders";

type Props = StepTypeDetailsProps<PartialWithRequiredType<SendSmsStep>>;

type VariantCreationType = "scratch" | "existing";

export function SendSmsTemplatePreview({
  step,
  schemaType,
  category,
  setStep,
  save,
  invalidSteps,
  setSelectedId,
  showValidationErrors,
  automationId,
}: Props) {
  const { t } = useTranslation();
  const templateId = step.templateId;
  const stepId = step.id;
  const navigate = useNavigate();
  const location = useLocation();

  const team = useRequiredContext(TeamContext);
  const smsTemplatesRpcClient = useRequiredContext(
    RedoMerchantSmsTemplatesRpcClientContext,
  );

  const otherInvalidSteps = invalidSteps?.filter((s) => s.id !== stepId) || [];
  const hasOtherInvalidSteps = !!otherInvalidSteps.length;

  const [smsTemplateName, setSmsTemplateName] = useState<string>("");
  const [showSmsContentDialog, setShowSmsContentDialog] =
    useState<boolean>(false);
  const [selectedOption, setSelectedOption] =
    useState<VariantCreationType>("scratch");
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(
    null,
  );
  const [smsNameTouched, setSmsNameTouched] = useState(
    (showValidationErrors && !hasOtherInvalidSteps) || false,
  );
  const [contentButtonClicked, setContentButtonClicked] = useState(
    showValidationErrors || false,
  );

  // Variant modal extra fields (SMS-specific)
  const [variantSelectedOption, setVariantSelectedOption] =
    useState<VariantCreationType>("scratch");
  const [variantSelectedTemplate, setVariantSelectedTemplate] =
    useState<SmsTemplate | null>(null);

  // Load SMS template
  const smsTemplateLoad = useLoad(async () => {
    if (!templateId) {
      return null;
    }
    try {
      const { template } =
        await smsTemplatesRpcClient.getSmsTemplateByIdPopulated({
          id: templateId,
        });
      return template;
    } catch {
      return null;
    }
  }, [templateId, smsTemplatesRpcClient]);

  const smsTemplate = smsTemplateLoad.value;
  const isTemplateDeleted = templateId && smsTemplate === null;

  // Load all SMS templates for the "from existing" dropdown
  const smsTemplatesLoad = useLoad(async () => {
    const templates = await smsTemplatesRpcClient.paginateSmsTemplates({
      page: 1,
      pageSize: 1000,
    });
    return templates.data;
  }, [smsTemplatesRpcClient]);

  const smsTemplates = smsTemplatesLoad.value || [];
  const templateOptions: RedoListItem<SmsTemplate>[] = smsTemplates.map(
    (template) => ({
      id: template._id,
      type: "text",
      text: template.name,
      value: template,
    }),
  );

  // Create template function for A/B test variants
  const createVariantTemplate = async (name: string) => {
    const useExisting =
      variantSelectedOption === "existing" && !!variantSelectedTemplate;

    let defaultTemplate: Omit<SmsTemplate, "_id"> = {
      team: team._id,
      name,
      content: "",
      templateType: TemplateType.MARKETING,
      schemaType,
      category,
      autoShortenLinks: true,
    };

    if (useExisting && variantSelectedTemplate) {
      defaultTemplate = {
        ...defaultTemplate,
        content: variantSelectedTemplate.content || "",
        prefixContent: variantSelectedTemplate.prefixContent,
        attachments: variantSelectedTemplate.attachments,
      };
    }

    const response = await smsTemplatesRpcClient.createSmsTemplate({
      template: defaultTemplate,
    });
    return response._id;
  };

  // A/B test editor hook
  const abTest = useAbTestEditor({
    automationId,
    stepId,
    stepType: "send_sms",
    splitId: step.splitId,
    existingTemplateId: templateId,
    existingTemplateName: smsTemplate?.name,
    onSplitIdChange: (splitId) => setStep({ ...step, splitId }),
    onSave: save,
    createTemplate: createVariantTemplate,
    getEditPath: (tplId, returnPath) =>
      `../../email-sms/templates/sms/${tplId}?${returnPath}&stepType=${step.type}`,
    getViewPath: (tplId, returnPath) =>
      `../../email-sms/templates/sms/${tplId}?${returnPath}&stepType=${step.type}&readonly=true`,
  });

  const renderVariantPreview = (treatment: Treatment) => {
    if (!treatment.templateId) {
      return <VariantPlaceholder hasTemplate={false} type="template" />;
    }
    return <SmsPreviewContent templateId={treatment.templateId} />;
  };

  // Handle opening variant modal - reset extra fields
  const handleOpenVariantModal = () => {
    setVariantSelectedOption("scratch");
    setVariantSelectedTemplate(null);
    const variantLetter = String.fromCharCode(
      65 + (abTest.splitWithTreatments?.treatments.length ?? 1),
    );
    const variantName = `Variant ${variantLetter}`;
    abTest.variantModal.open(
      variantName,
      t("{{variantName}} Template", { variantName }),
    );
  };

  const smsNameError = useMemo(() => {
    if (
      !templateId &&
      !isTemplateDeleted &&
      !smsTemplateName &&
      smsNameTouched
    ) {
      return t("SMS name is required");
    }
    return undefined;
  }, [smsTemplateName, templateId, isTemplateDeleted, smsNameTouched, t]);

  useEffect(() => {
    if (selectedOption === "scratch") {
      setSelectedTemplate(null);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (isTemplateDeleted) {
      setStep({ ...step, templateId: undefined });
    }
  }, [isTemplateDeleted, setStep, step]);

  const handleEditTemplate = async () => {
    if (!smsTemplateLoad.value) {
      return;
    }
    navigate(
      `../../email-sms/templates/sms/${smsTemplateLoad.value._id.toString()}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}&stepType=${step.type}`,
    );
  };

  const handleCreateTemplateClick = () => {
    if (smsTemplateName) {
      setShowSmsContentDialog(true);
    }
  };

  const handleCreateTemplate = async (useExistingTemplate = false) => {
    try {
      let defaultTemplate: Omit<SmsTemplate, "_id"> = {
        team: team._id,
        name: smsTemplateName,
        content: "",
        templateType: TemplateType.MARKETING,
        schemaType,
        category,
        autoShortenLinks: true,
      };

      if (useExistingTemplate && selectedTemplate) {
        defaultTemplate = {
          ...defaultTemplate,
          content: selectedTemplate.content || "",
          prefixContent: selectedTemplate.prefixContent,
          attachments: selectedTemplate.attachments,
        };
      }

      const response = await smsTemplatesRpcClient.createSmsTemplate({
        template: defaultTemplate,
      });

      setStep({ ...step, templateId: response._id });
      save?.();
      setShowSmsContentDialog(false);

      navigate(
        `../../email-sms/templates/sms/${response._id.toString()}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}&stepType=${step.type}`,
      );
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("409")) {
          toast(t("SMS name already exists. Please use a different name."), {
            variant: "error",
          });
        } else {
          toast(
            t("Failed to create template: {{message}}", {
              message: error.message,
            }),
            { variant: "error" },
          );
        }
      } else {
        toast(t("Failed to create template"), { variant: "error" });
      }
    }
  };

  const handleSelectWinner = (winningTemplateId: string) => {
    setStep({ ...step, templateId: winningTemplateId, splitId: undefined });
    save?.();
    abTest.refetch();
  };

  const imageAttachment = getImageAttachment(smsTemplate);

  const fullSmsTemplateTextContent = useMemo(() => {
    if (!smsTemplate) {
      return "";
    }
    if (smsTemplate.aiConfig?.enabled && smsTemplate.aiConfig?.contentPreview) {
      return smsTemplate.aiConfig.contentPreview;
    }
    return `${smsTemplate.prefixContent || ""}${smsTemplate.content || ""}`;
  }, [smsTemplate]);

  const templateHasContent = useMemo(() => {
    if (!smsTemplate) {
      return false;
    }
    return !!(
      fullSmsTemplateTextContent.trim().length ||
      imageAttachment ||
      smsTemplate.aiConfig?.contentPreview
    );
  }, [smsTemplate, fullSmsTemplateTextContent, imageAttachment]);

  const delayedAutoFocus = useDelayedAutoFocus(!hasOtherInvalidSteps);
  const delayedAutoFocusInvalidSteps =
    useDelayedAutoFocus(hasOtherInvalidSteps);

  const hasTemplate = !!templateId;

  // Render based on A/B test view
  if (abTest.view === "loading") {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (abTest.view === "results" && automationId) {
    return (
      <AutomationAbTestResultsPanel
        automationId={automationId}
        automationStepId={stepId}
        icon={<MessageChatCircleIcon height={20} width={20} />}
        onSelectWinner={handleSelectWinner}
        onViewVariant={abTest.viewVariant}
        renderVariantPreview={renderVariantPreview}
        stepType="send_sms"
        title={t("Send SMS A/B test")}
      />
    );
  }

  if (abTest.view === "editor" && abTest.splitWithTreatments) {
    return (
      <>
        <AutomationAbTestEditor
          icon={<MessageChatCircleIcon height={20} width={20} />}
          onAddVariant={handleOpenVariantModal}
          onClose={abTest.closeEditor}
          onEditVariant={abTest.editVariant}
          renderVariantPreview={renderVariantPreview}
          splitWithTreatments={abTest.splitWithTreatments}
          stepType="send_sms"
          title={t("Send SMS A/B test")}
        />

        {/* Variant template creation modal */}
        <RedoModal
          isOpen={abTest.variantModal.isOpen}
          modalSize="md"
          onModalCloseRequested={abTest.variantModal.close}
          primaryButton={{
            text: t("Continue"),
            onClick: abTest.variantModal.create,
            disabled:
              !abTest.variantModal.templateName ||
              (variantSelectedOption === "existing" &&
                !variantSelectedTemplate),
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: abTest.variantModal.close,
          }}
          title={t("Create template for {{variantName}}", {
            variantName: abTest.variantModal.variantName,
          })}
        >
          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                {t("Template name")}
              </Text>
              <RedoTextInput
                placeholder={t("Enter template name")}
                setValue={abTest.variantModal.setTemplateName}
                size="sm"
                value={abTest.variantModal.templateName}
              />
            </Flex>

            <RedoCheckboxCard
              checked={variantSelectedOption === "scratch"}
              inputType="radio"
              layout={Layout.SHORT}
              onClick={() => setVariantSelectedOption("scratch")}
              title={t("Create from scratch")}
            >
              {t("Start from a blank template.")}
            </RedoCheckboxCard>

            <Flex dir="column" gap="lg">
              <RedoCheckboxCard
                checked={variantSelectedOption === "existing"}
                inputType="radio"
                layout={Layout.SHORT}
                onClick={() => setVariantSelectedOption("existing")}
                title={t("Start from existing SMS")}
              >
                {t("Copy content from an existing SMS template.")}
              </RedoCheckboxCard>

              {variantSelectedOption === "existing" && (
                <Flex dir="column" gap="xs" ml="md">
                  <Text fontSize="xs" textColor="secondary">
                    {t("Existing SMS templates")}
                  </Text>
                  <RedoSingleSelectDropdownInput
                    disabled={templateOptions.length === 0}
                    emptyListMessage={t("No SMS templates available")}
                    options={templateOptions}
                    optionSelected={({ item }) => {
                      setVariantSelectedTemplate(item.value);
                    }}
                    placeholder={t("Select a template")}
                    selectedItem={templateOptions.find(
                      (t) =>
                        variantSelectedTemplate &&
                        t.value._id === variantSelectedTemplate._id,
                    )}
                    size="xs"
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        </RedoModal>
      </>
    );
  }

  // Loading state for template data
  if (smsTemplate === undefined && !isTemplateDeleted) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  // Normal inspector view
  return (
    <InspectorLayout title={t("Send SMS")}>
      <InspectorSection label={t("SMS")}>
        <InspectorField label={t("SMS name")}>
          <RedoTextInput
            autoFocus={delayedAutoFocus}
            errorText={smsNameError}
            key={delayedAutoFocus.toString()}
            onBlur={() => setSmsNameTouched(true)}
            placeholder={t("Add a name")}
            setValue={setSmsTemplateName}
            size="sm"
            state={
              (templateId && !isTemplateDeleted) || hasOtherInvalidSteps
                ? "readonly"
                : "default"
            }
            value={smsTemplate?.name || smsTemplateName}
          />
        </InspectorField>

        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" textColor="secondary">
              {t("Content")}
            </Text>
            {templateId && !isTemplateDeleted && templateHasContent && (
              <RedoButton
                disabled={hasOtherInvalidSteps}
                hierarchy="secondary"
                IconLeading={EditPencilIcon}
                onClick={handleEditTemplate}
                size="xs"
                text={t("Edit")}
              />
            )}
          </Flex>

          <Flex
            bgColor="primary"
            borderColor={
              !templateId && !templateHasContent && contentButtonClicked
                ? "error"
                : "primary"
            }
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            p="sm"
            position="relative"
            w="full"
          >
            {hasOtherInvalidSteps && setSelectedId ? (
              <InvalidStepsList
                autoFocus={delayedAutoFocusInvalidSteps}
                invalidSteps={otherInvalidSteps}
                key={delayedAutoFocusInvalidSteps.toString()}
                onGoToStep={setSelectedId}
              />
            ) : templateId && templateHasContent ? (
              <SmsPreviewContent templateId={templateId} />
            ) : (
              <Flex
                align="center"
                bgColor="primary"
                height="half-screen"
                justify="center"
                maxHeight="half-screen"
              >
                {smsTemplate && !templateHasContent ? (
                  <RedoButton
                    hierarchy="secondary"
                    IconLeading={PlusIcon}
                    onClick={handleEditTemplate}
                    text={t("Add Content")}
                  />
                ) : (
                  <RedoButton
                    disabled={!smsTemplateName}
                    hierarchy="secondary"
                    IconLeading={PlusIcon}
                    onClick={handleCreateTemplateClick}
                    text={t("Content")}
                    theme="action"
                  />
                )}
              </Flex>
            )}
          </Flex>

          {!templateId && !templateHasContent && contentButtonClicked && (
            <Text fontSize="xs" textColor="error">
              {hasOtherInvalidSteps
                ? t("Fix invalid steps before adding content", {
                    count: otherInvalidSteps.length,
                  })
                : !smsTemplateName
                  ? t("Add SMS name before adding content")
                  : t("Content is required")}
            </Text>
          )}
        </Flex>
      </InspectorSection>

      <InspectorSection label={t("Step")}>
        <InspectorField label={null}>
          <RedoToggle
            label={t("Deactivate")}
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>
      </InspectorSection>

      {/* A/B test panel */}
      {automationId && (
        <AutomationAbTestInspector
          existingTest={abTest.splitWithTreatments}
          hasTemplate={hasTemplate}
          onOpenEditor={abTest.openEditor}
        />
      )}

      {showSmsContentDialog && (
        <RedoModal
          isOpen={showSmsContentDialog}
          modalSize="md"
          onModalCloseRequested={() => {
            setShowSmsContentDialog(false);
            setContentButtonClicked(true);
          }}
          primaryButton={{
            text: t("Continue"),
            onClick: () =>
              handleCreateTemplate(
                selectedOption === "existing" && !!selectedTemplate,
              ),
            disabled: selectedOption === "existing" && !selectedTemplate,
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: () => {
              setShowSmsContentDialog(false);
              setContentButtonClicked(true);
            },
          }}
          title={t("SMS Content")}
        >
          <Flex dir="column" gap="lg">
            <RedoCheckboxCard
              checked={selectedOption === "scratch"}
              inputType="radio"
              layout={Layout.SHORT}
              onClick={() => setSelectedOption("scratch")}
              title={t("Create from scratch")}
            >
              {t("Start from a totally blank canvas and bring it to life.")}
            </RedoCheckboxCard>
            <Flex dir="column" gap="lg">
              <RedoCheckboxCard
                checked={selectedOption === "existing"}
                inputType="radio"
                layout={Layout.SHORT}
                onClick={() => setSelectedOption("existing")}
                title={t("Start from existing SMS")}
              >
                {t(
                  "Pick from any of your saved SMS templates and build from there.",
                )}
              </RedoCheckboxCard>

              {selectedOption === "existing" && (
                <Flex dir="column" gap="xs" ml="md">
                  <Text fontSize="xs" textColor="secondary">
                    {t("Existing SMS templates")}
                  </Text>
                  <RedoSingleSelectDropdownInput
                    disabled={templateOptions.length === 0}
                    emptyListMessage={t("No SMS templates available")}
                    options={templateOptions}
                    optionSelected={({ item }) => {
                      setSelectedTemplate(item.value);
                    }}
                    placeholder={t("Select a template")}
                    selectedItem={templateOptions.find(
                      (t) =>
                        selectedTemplate &&
                        t.value._id === selectedTemplate._id,
                    )}
                    size="xs"
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        </RedoModal>
      )}
    </InspectorLayout>
  );
}
